import axios from "axios";
import { getCookie } from "@util/cookie";
import type { MetaPagination } from "@type/common";
import type {
    PendingHrTrigger,
    WhatsappThreadMessage,
    WhatsappThreadResult,
} from "@type/whatsapp";

const apiURL = import.meta.env.VITE_TALENT_AI_BE_BASE_URL;

// NOTE: the former VITE_WHATSAPP_OPTIN_ENABLED env ship-gate was removed — the
// per-company `company_whatsapp_enabled` field on the job detail endpoints is
// now the single gate for showing the opt-in checkbox (BE only enables it where
// the WhatsApp routes are live).

/** Where the opt-in consent was captured (matches the `whatsapp_opt_ins.source` enum, BE #3353). */
export type WhatsappOptInSource = "application_form" | "accept_step" | "manual";

export interface WhatsappOptInRequest {
    /** true → consent given; false → no opted-in row created. */
    opted_in: boolean;
    /** Candidate phone in E.164, e.g. `+60123456789` (with country code, no spaces). */
    phone_e164: string;
    source: WhatsappOptInSource;
}

/**
 * Persist a candidate's WhatsApp opt-in consent — the Phase-1 compliance gate
 * (no production WhatsApp send happens without an opt-in row).
 *
 * Contract (BE ourteam-backend#3406 — verified LIVE on staging; confirm on prod
 * before the prod release, WhatsApp router not yet deployed to prod as of 2026-07-01):
 *   PATCH /whatsapp/opt-in/{candidate_id}  body { opted_in, phone_e164, source }
 *   → upserts a `whatsapp_opt_ins` row (unique on candidate_id + phone_e164),
 *     so re-submitting updates the existing row rather than duplicating.
 *
 * Best-effort by design — a failure here must never block the application submit.
 */
export const markWhatsappOptIn = async (
    candidateId: string,
    payload: WhatsappOptInRequest
): Promise<any> => {
    const token = getCookie("authToken") || getCookie("tempAuthToken");
    return await axios({
        url: `${apiURL}/whatsapp/opt-in/${candidateId}`,
        method: "PATCH",
        headers: {
            "Content-Type": "application/json",
            ...(token ? { Authorization: `Bearer ${token}` } : {}),
        },
        data: payload,
    });
};

/**
 * Build an E.164 phone string from a country dial code (e.g. `+60`) and a local
 * number. Strips non-digits and a leading trunk `0` from the local part, so
 * `("+62", "08123…")` → `+628123…`.
 */
export function buildE164(countryCode: string, localNumber: string): string {
    const cc = (countryCode || "").trim();
    const code = cc.startsWith("+") ? cc : `+${cc.replace(/\D/g, "")}`;
    const local = (localNumber || "").replace(/\D/g, "").replace(/^0+/, "");
    return `${code}${local}`;
}

/** E.164: a `+`, a leading 1–9, then 6–14 more digits (7–15 digits total). */
export function isValidE164(value: string): boolean {
    return /^\+[1-9]\d{6,14}$/.test(value);
}

/**
 * Thrown when the HR isn't allowed to read this candidate's thread (403 — a
 * different company's HR, or a non-organization role). No thread data should be
 * rendered in that case; callers show an access-denied / empty state instead.
 */
export class WhatsappThreadForbiddenError extends Error {
    constructor(message = "You do not have access to this candidate's messages.") {
        super(message);
        this.name = "WhatsappThreadForbiddenError";
    }
}

/**
 * Fetch one page of a candidate's WhatsApp thread with the HR's company.
 *
 * Contract (BE feature/whatsapp-integration-stg, PR to staging pending):
 *   GET /employer/candidate/{candidateId}/whatsapp-thread?page=&limit=
 *   → StandardResponse; `data` is the message list OLDEST → NEWEST within the
 *     page, `meta.pagination` carries { total, page, limit, total_pages }.
 *
 * Auth: HR of the candidate's company only; the thread is company-scoped
 * server-side so a candidate's messages with another company never leak. A 403
 * envelope surfaces as WhatsappThreadForbiddenError.
 *
 * SIDE EFFECT: fetching a page marks that page's inbound (candidate) messages
 * read server-side (`hr_read: true`). The rows returned still reflect their
 * pre-fetch `hr_read`, so the caller can derive the unread indicator from this
 * same response — no separate mark-read call exists or is needed.
 */
export async function getCandidateWhatsappThread(
    candidateId: string,
    page = 1,
    limit = 50
): Promise<WhatsappThreadResult> {
    const token = getCookie("authToken");
    try {
        const res = await axios.get(
            `${apiURL}/employer/candidate/${candidateId}/whatsapp-thread`,
            {
                params: { page, limit },
                headers: {
                    "Content-Type": "application/json",
                    ...(token ? { Authorization: `Bearer ${token}` } : {}),
                },
            }
        );
        const body = res.data as {
            code?: number;
            message?: string;
            data?: WhatsappThreadMessage[];
            meta?: { pagination?: MetaPagination; pending_hr_trigger?: PendingHrTrigger | null };
            pending_hr_trigger?: PendingHrTrigger | null;
        };
        if (body?.code === 403) {
            throw new WhatsappThreadForbiddenError(body.message);
        }
        const messages = Array.isArray(body?.data) ? body.data : [];
        const pagination: MetaPagination = body?.meta?.pagination ?? {
            total: messages.length,
            page,
            limit,
            total_pages: 1,
        };
        // #3242 (BE ourteam-backend#3981): armed HR step-trigger. `data` is the
        // message list, so the field rides the envelope — proposed home is
        // `meta.pending_hr_trigger`; the top-level read covers the alternative
        // until the BE pins the position. Absent/old BE ⇒ null ⇒ banner hidden.
        const pendingHrTrigger = body?.meta?.pending_hr_trigger ?? body?.pending_hr_trigger ?? null;
        return { messages, pagination, pending_hr_trigger: pendingHrTrigger };
    } catch (err) {
        if (err instanceof WhatsappThreadForbiddenError) throw err;
        const status = (err as { response?: { status?: number } })?.response
            ?.status;
        if (status === 403) {
            const msg = (err as { response?: { data?: { message?: string } } })
                ?.response?.data?.message;
            throw new WhatsappThreadForbiddenError(msg);
        }
        throw err;
    }
}

// Why a reply couldn't be sent, mapped from the BE status codes so the composer
// can react precisely (disable + explain on a closed window, etc.).
export type WhatsappReplyErrorKind =
    | "window_closed" // 409 — the 24h WhatsApp free-form window is closed
    | "no_optin"      // 404 — candidate has no active opt-in for this company
    | "empty"         // 422 — empty message
    | "twilio"        // 502 — Twilio rejected the send
    | "generic";      // anything else

export class WhatsappReplyError extends Error {
    kind: WhatsappReplyErrorKind;
    constructor(kind: WhatsappReplyErrorKind, message: string) {
        super(message);
        this.name = "WhatsappReplyError";
        this.kind = kind;
    }
}

export interface WhatsappReplyResult {
    id: string;
    status: string;
    wa_message_sid: string;
}

const REPLY_ERROR_BY_STATUS: Record<number, { kind: WhatsappReplyErrorKind; fallback: string }> = {
    409: {
        kind: "window_closed",
        fallback:
            "You can only reply within 24 hours of the candidate's last message (WhatsApp policy). Wait for them to message again.",
    },
    404: {
        kind: "no_optin",
        fallback: "This candidate hasn't opted in to WhatsApp for your company.",
    },
    422: { kind: "empty", fallback: "Please type a message before sending." },
    502: {
        kind: "twilio",
        fallback: "WhatsApp rejected the message. Please try again in a moment.",
    },
};

/**
 * Send an HR free-form reply into the candidate's WhatsApp thread.
 *
 * Contract (BE, per ticket #3017 update — same staging PR as the thread):
 *   POST /employer/candidate/{candidateId}/whatsapp-reply  body { message }
 *   → 200 { id, status, wa_message_sid } — appears as an outbound row
 *     (`event: "hr_reply"`) on the next thread fetch.
 *   → 409 window closed · 404 no opt-in · 422 empty · 502 Twilio rejected.
 * HR write-role only. Errors surface as a typed WhatsappReplyError.
 */
export async function sendWhatsappReply(
    candidateId: string,
    message: string
): Promise<WhatsappReplyResult> {
    const token = getCookie("authToken");
    try {
        const res = await axios.post(
            `${apiURL}/employer/candidate/${candidateId}/whatsapp-reply`,
            { message },
            {
                headers: {
                    "Content-Type": "application/json",
                    ...(token ? { Authorization: `Bearer ${token}` } : {}),
                },
            }
        );
        const body = res.data as {
            code?: number;
            message?: string;
            data?: WhatsappReplyResult;
        };
        // Tolerate an in-envelope error code (200 HTTP + code: 4xx).
        if (typeof body?.code === "number" && body.code >= 400) {
            const mapped = REPLY_ERROR_BY_STATUS[body.code];
            throw new WhatsappReplyError(
                mapped?.kind ?? "generic",
                body.message || mapped?.fallback || "Couldn't send the reply."
            );
        }
        return (
            body?.data ?? { id: "", status: "queued", wa_message_sid: "" }
        );
    } catch (err) {
        if (err instanceof WhatsappReplyError) throw err;
        const status = (err as { response?: { status?: number } })?.response?.status;
        const beMessage = (err as { response?: { data?: { message?: string } } })
            ?.response?.data?.message;
        const mapped = status ? REPLY_ERROR_BY_STATUS[status] : undefined;
        throw new WhatsappReplyError(
            mapped?.kind ?? "generic",
            beMessage || mapped?.fallback || "Couldn't send the reply. Please try again."
        );
    }
}
